import type { NavigationGuard } from 'vue-router';

import { dataFrom } from '@zyno-io/openapi-client-codegen';

import { SuiteCoreConnectedAppsApi } from '@/openapi-clients-generated/suite';
import { Auth } from '@/shared/services/auth.service';

import { type CallbackParams, parseCallbackQuery, pruneAttempts, saveAttempt } from './helpers';

/**
 * Starts a Slack authorization and leaves the app for Slack. Passing the existing connection's name on a
 * reconnect keeps it; otherwise the API names the connection after the workspace. The attempt is saved
 * before navigating so the callback can verify it started here, in this tenant.
 */
export async function startSlackConnect(name?: string) {
    const { authorizationUrl, state } = dataFrom(
        await SuiteCoreConnectedAppsApi.postCoreConnectedAppsStartSlackConnect({ body: name ? { name } : {} })
    );
    pruneAttempts(sessionStorage);
    saveAttempt(sessionStorage, state, {
        provider: 'slack',
        tenantId: Auth.tenantId.value!,
        impersonatorTenantId: Auth.impersonatorTenantId.value,
        createdAt: Date.now()
    });
    window.location.assign(authorizationUrl);
}

let capturedCallback: CallbackParams | null = null;

/**
 * Route guard for the callback: lifts `code`/`state`/`error` out of the URL before the screen mounts, so the
 * authorization code isn't left in the address bar, history, or anything that later reads the route.
 */
export const captureSlackCallback: NavigationGuard = to => {
    if (!Object.keys(to.query).length) return true;
    capturedCallback = parseCallbackQuery(to.query);
    return { path: to.path, query: {}, hash: to.hash, replace: true };
};

/** Hands the captured callback to the screen exactly once. */
export function takeCapturedCallback() {
    const params = capturedCallback;
    capturedCallback = null;
    return params;
}
